/**
 * useServices — the React binding for the Service Directory.
 *
 * Strict layering: the page calls THIS hook; the hook calls {@link ServiceStore}
 * (and the existing Goal / Workstream stores plus the sibling TeamDocument /
 * Decision stores) to resolve linked-entity titles + derive "who knows this
 * system?" CLIENT-SIDE; the stores call the api facade. The page never imports a
 * store or the api directly.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { GoalStore } from '../goals/goalStore';
import { WorkstreamStore } from '../workstreams/workstreamStore';
import { TeamDocumentStore } from '../documents/documentStore';
import { DecisionStore } from '../decisions/decisionStore';
import { ServiceStore } from './serviceStore';
import { sortServices } from './serviceModel';
import type { Service, ServiceDraft, ServiceSummary } from './types';
import type { GoalSummary } from '../goals/types';
import type { WorkstreamSummary } from '../workstreams/types';
import type { TeamDocumentSummary } from '../documents/types';
import type { DecisionSummary } from '../decisions/types';

export interface UseServicesResult {
  services: ServiceSummary[];
  goals: GoalSummary[];
  workstreams: WorkstreamSummary[];
  documents: TeamDocumentSummary[];
  decisions: DecisionSummary[];
  currentAlias: string;
  goalTitles: Map<string, string>;
  workstreamTitles: Map<string, string>;
  documentTitles: Map<string, string>;
  decisionTitles: Map<string, string>;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  get: (id: string) => Promise<Service | undefined>;
  create: (draft: ServiceDraft) => Promise<Service>;
  update: (id: string, draft: ServiceDraft) => Promise<Service | undefined>;
  remove: (id: string) => Promise<void>;
}

export function useServices(): UseServicesResult {
  const api = useApi();
  const stores = useMemo(
    () => ({
      services: new ServiceStore(api),
      goals: new GoalStore(api),
      workstreams: new WorkstreamStore(api),
      documents: new TeamDocumentStore(api),
      decisions: new DecisionStore(api),
    }),
    [api],
  );

  const [services, setServices] = useState<ServiceSummary[]>([]);
  const [goals, setGoals] = useState<GoalSummary[]>([]);
  const [workstreams, setWorkstreams] = useState<WorkstreamSummary[]>([]);
  const [documents, setDocuments] = useState<TeamDocumentSummary[]>([]);
  const [decisions, setDecisions] = useState<DecisionSummary[]>([]);
  const [currentAlias, setCurrentAlias] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [serviceRows, goalRows, wsRows, docRows, decisionRows, identity] =
        await Promise.all([
          stores.services.listSummaries(),
          stores.goals.listSummaries(),
          stores.workstreams.listSummaries(),
          stores.documents.listSummaries(),
          stores.decisions.listSummaries(),
          api.identityProvider.getIdentity(),
        ]);
      setServices(sortServices(serviceRows));
      setGoals(goalRows);
      setWorkstreams(wsRows);
      setDocuments(docRows);
      setDecisions(decisionRows);
      setCurrentAlias(identity.alias);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load services.');
    } finally {
      setLoading(false);
    }
  }, [stores, api]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const goalTitles = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of goals) m.set(g.id, g.title);
    return m;
  }, [goals]);

  const workstreamTitles = useMemo(() => {
    const m = new Map<string, string>();
    for (const w of workstreams) m.set(w.id, w.title);
    return m;
  }, [workstreams]);

  const documentTitles = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of documents) m.set(d.id, d.title);
    return m;
  }, [documents]);

  const decisionTitles = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of decisions) m.set(d.id, d.decision);
    return m;
  }, [decisions]);

  const get = useCallback((id: string) => stores.services.get(id), [stores]);

  const create = useCallback(
    async (draft: ServiceDraft) => {
      const saved = await stores.services.create(draft);
      await refresh();
      return saved;
    },
    [stores, refresh],
  );

  const update = useCallback(
    async (id: string, draft: ServiceDraft) => {
      const existing = await stores.services.get(id);
      if (!existing) return undefined;
      const saved = await stores.services.applyDraft(existing, draft);
      await refresh();
      return saved;
    },
    [stores, refresh],
  );

  const remove = useCallback(
    async (id: string) => {
      await stores.services.softDelete(id);
      await refresh();
    },
    [stores, refresh],
  );

  return {
    services,
    goals,
    workstreams,
    documents,
    decisions,
    currentAlias,
    goalTitles,
    workstreamTitles,
    documentTitles,
    decisionTitles,
    loading,
    error,
    refresh,
    get,
    create,
    update,
    remove,
  };
}
